import { useEffect, useState } from 'react';

import { useLocation } from 'react-router-dom';
import { IconRefresh } from '@tabler/icons-react';

import { TableContainer } from '../../Styles/CustomViews/Pages/ListComponent/customListComponentStyles';
import { InstanceDetailsContainer, SPTableContainer, StoredProcedureLegendContainer } from '../../Styles/InstanceManagement/instanceManagementStyles';

import LocalStorageHelper from '../../Utilities/Helpers/LocalStorageHelper';
import QueryBuilderHelper from '../../Utilities/Helpers/QueryBuilderHelper';
import DateHelper from '../../Utilities/Helpers/DateHelper';

import { CachingEnum } from '../../Utilities/Enums/CachingEnum';
import { OrderByEnum } from '../../Utilities/Enums/SortEnums';
import { InstanceStoredProceduresListField } from '../../Utilities/Enums/ParametersEnum';
import { InstanceQueryTypeEnum, InstanceStoredProcedureStatusEnum, InstanceStoredProcedureStatusTextEnum, SpStatus } from '../../Utilities/Enums/StatusEnums';

import CustomIconButton from '../../CustomViews/Elements/CustomIconButton';
import CustomListComponent from '../../CustomViews/Pages/ListComponent/CustomListComponent';

import { InstanceStoredProcedureInterface, InstanceStoredProceduresList } from '../../Models/InstanceManagement/InstanceManagement';

import { useInstanceManagementHook } from '../../Hooks/InstanceManagement/instanceManagementHooks';

export default function InstanceStoredProcedures(props: InstanceStoredProcedureInterface) {
    let { triggeredQuery, settriggeredQuery } = props;
    let location = useLocation();
    let queryParams = new URLSearchParams(location.search);
    let instanceId = queryParams.get('id') ?? "0";
    let isDarkMode = LocalStorageHelper.isDarkMode();

    // states
    const [queryStr, setQueryStr] = useState({
        pageSize: QueryBuilderHelper.DefaultPageSizeValue(),
        pageNumber: QueryBuilderHelper.DefaultPageNumValue(),
        searchString: QueryBuilderHelper.DefaultSearchValue(),
        status: QueryBuilderHelper.DefaultQueryFilterValue('status') ?? 0,
        sortBy: QueryBuilderHelper.DefaultSortValue(InstanceStoredProceduresListField.CREATED_DATE),
        orderBy: QueryBuilderHelper.DefaultOrderValue(OrderByEnum.DESC),
    });

    const q_queryKey = [CachingEnum.INSTANCE_STORED_PROCEDURE_LIST, queryStr]
    const { data, fetchStatus, instancesInvalidateQueries } = useInstanceManagementHook(q_queryKey, queryStr, true, InstanceQueryTypeEnum.STORED_PROCEDURES, instanceId);
    
    const tableColumns = [
        {
            accessor: InstanceStoredProceduresListField.NAME,
            title: 'Procedure',
            sortable: true,
            render: (record: InstanceStoredProceduresList) => <InstanceDetailsContainer $darkMode={isDarkMode}>
                <span className='spName'>{record?.[InstanceStoredProceduresListField.NAME] ?? "--"}</span>
                {record?.[InstanceStoredProceduresListField.ERROR_TEXT] != null && record?.[InstanceStoredProceduresListField.ERROR_TEXT] != "" && <span className='spErrorText'>{record?.[InstanceStoredProceduresListField.ERROR_TEXT] ?? "--"}</span>}
            </InstanceDetailsContainer>
        },
        {
            accessor: InstanceStoredProceduresListField.STATUS,
            title: 'Status',
            sortable: true,
            render: (record: InstanceStoredProceduresList) => {
                let status: InstanceStoredProcedureStatusEnum = record?.[InstanceStoredProceduresListField.STATUS] ?? InstanceStoredProcedureStatusEnum.VALID;
                let statusText = InstanceStoredProcedureStatusTextEnum[status];
                
                return <InstanceDetailsContainer $darkMode={isDarkMode}>
                    <span className={'spStatus ' + statusText.toLowerCase()}><span className='dotText'>&nbsp;</span>{statusText}</span>
                </InstanceDetailsContainer>
            }
        },
        {
            accessor: InstanceStoredProceduresListField.LAST_COMPILED,
            title: 'Last Compiled',
            sortable: true,
            render: (record: InstanceStoredProceduresList) => {
                let lastCompiled: string | Date | null = record?.[InstanceStoredProceduresListField.LAST_COMPILED] != null && record?.[InstanceStoredProceduresListField.LAST_COMPILED] != "" ? DateHelper.getFormattedDate(record?.[InstanceStoredProceduresListField.LAST_COMPILED], "dd/MM/yyyy") : null;
                
                return <InstanceDetailsContainer $darkMode={isDarkMode}>
                    <span className='spDateText'>{lastCompiled ?? '--'}</span>
                </InstanceDetailsContainer>
            }
        },
        {
            accessor: '#',
            title: "Action",
            render: (record: InstanceStoredProceduresList) => <>
                {record?.[InstanceStoredProceduresListField.STATUS] != InstanceStoredProcedureStatusEnum.VALID && <CustomIconButton toolTipName='Recompile'
                    icon={<IconRefresh />}
                    disabled
                    onClick={() => {
                        
                    }} />}
            </>
        },
    ];
    
    //Event Handlers
    useEffect(() => {
        instancesInvalidateQueries();
    }, [instanceId]);

    //Rendering Functions
    const renderLegends = () => {
        let legends: any = data?.legends ?? SpStatus;
        
        return <StoredProcedureLegendContainer $darkMode={isDarkMode}>
            {legends?.map((legend: any) => (
                <div className='legendItemContainer'>
                    <span className={`legendCount ${legend?.title?.toLowerCase() ?? ""}`}>{legend?.count ?? 0}</span>
                    <span className='legendTitle'>{legend?.title ?? "--"}</span>
                </div>
            ))}
        </StoredProcedureLegendContainer>
    }

    const renderTable = () => {
        return <SPTableContainer>
            {renderLegends()}

            <TableContainer elevation={2} $isNotBorderRadiusAll={true}>
                <CustomListComponent
                    fetching={(fetchStatus == "fetching" && triggeredQuery == CachingEnum.INSTANCE_STORED_PROCEDURE_LIST) || (fetchStatus == "fetching" && triggeredQuery == 'all')}
                    striped
                    columns={tableColumns}
                    idAccessor={(record: InstanceStoredProceduresList) => `instanceStoredProcedure${record?.[InstanceStoredProceduresListField.ROW_ID]}`}
                    records={data?.data?.list ?? []}
                    totalRecords={data?.data?.metaData?.totalCount ?? 0}
                    page={queryStr.pageNumber}
                    recordsPerPage={queryStr.pageSize}
                    defaultColumnSort={{ columnAccessor: queryStr?.sortBy ?? 'id', direction: queryStr?.orderBy ?? 'asc' }}
                    onSortStatusChange={((sort: any) => { 
                        if(settriggeredQuery) {
                            settriggeredQuery(CachingEnum.INSTANCE_STORED_PROCEDURE_LIST);
                        }

                        setQueryStr((prev: any) => ({ ...prev, sortBy: sort?.columnAccessor ?? "id", orderBy: sort?.direction ?? "asc", pageNumber: 1 }));
                    })}
                    setPage={(p: number) => setQueryStr((prev: any) => ({ ...prev, pageNumber: p }))}
                    onRecordsPerPageChange={(p: number) => setQueryStr((prev: any) => ({ ...prev, pageSize: p, pageNumber: 1 }))} />
            </TableContainer>
        </SPTableContainer>
    }

    return <>
        {renderTable()}
    </>
}
